<template>
  <div>
    <p class="ck-subtitulo mb-3" style="max-width: 860px">
      Veja o horário previsto de cada dia do colaborador e ajuste o que foge da jornada: <b>horário especial</b> num dia,
      <b>folga avulsa</b> ou <b>troca de folga</b> (ex.: trocar o sábado). As exceções mudam só o horário previsto usado
      no espelho de ponto. Elas não alteram marcações e não abonam faltas: para abonar, use as justificativas.
    </p>

    <div class="card mb-3">
      <div class="card-body pb-2">
        <div class="row g-2 align-items-end">
          <div class="col-lg-4 col-md-6">
            <label>Colaborador</label>
            <select id="esc-colab" data-placeholder="Selecione o colaborador" data-allow-clear="true" style="width:100%"></select>
          </div>
          <div class="col-lg-3 col-md-6">
            <label>Mês</label>
            <div class="input-group">
              <button class="btn btn-light" @click="mudarMes(-1)" title="Mês anterior"><i class="bi bi-chevron-left"></i></button>
              <input type="month" v-model="mes" class="form-control text-center">
              <button class="btn btn-light" @click="mudarMes(1)" title="Próximo mês"><i class="bi bi-chevron-right"></i></button>
            </div>
          </div>
          <div class="col-lg-5 d-flex gap-2 flex-wrap justify-content-lg-end mb-2">
            <button class="btn btn-info" :disabled="!idcolaborador" @click="abrirExcecao()"><i class="bi bi-calendar-plus"></i> Nova exceção</button>
            <button class="btn btn-light" :disabled="!idcolaborador" @click="abrirTroca()"><i class="bi bi-arrow-left-right"></i> Trocar folga</button>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3">
      <!-- Calendário -->
      <div class="col-xl-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>{{ tituloMes }}</strong>
            <span class="legenda d-none d-sm-flex">
              <span class="lg trab"></span> trabalho <span class="lg folga"></span> folga <span class="lg fer"></span> feriado <span class="lg exc"></span> exceção
            </span>
          </div>
          <div class="card-body">
            <div v-if="!idcolaborador" class="text-center text-muted py-5">
              <i class="bi bi-person-badge" style="font-size: 2rem"></i>
              <p class="mt-2 mb-0">Selecione um colaborador para ver a escala do mês.</p>
            </div>
            <div v-else-if="carregando" class="text-center py-5"><span class="spinner-border"></span></div>
            <template v-else>
              <div v-if="!temEscala" class="alert alert-warning py-2 small">
                Este colaborador não tem jornada no período. Defina uma em <b>Escala do colaborador</b>, ao lado.
              </div>
              <div class="cal">
                <div v-for="n in SEMANA" :key="n" class="cal-cab">{{ n }}</div>
                <div v-for="(c, i) in celulas" :key="i"
                     :class="['cal-dia', { vazio: !c, folga: c && !c.horario, fer: c && c.feriado && c.feriado.aplicado, exc: c && c.excecao, hoje: c && c.data === hoje }]"
                     :title="c ? dica(c) : ''" @click="c && abrirExcecao(c)">
                  <template v-if="c">
                    <div class="d-flex justify-content-between">
                      <span class="num-dia">{{ Number(c.data.substring(8)) }}</span>
                      <small v-if="c.rotulo && c.rotulo.startsWith('Sem')" class="rot">{{ c.rotulo.split(' · ')[0] }}</small>
                      <small v-else-if="c.rotulo && c.rotulo.startsWith('Dia')" class="rot">{{ c.rotulo }}</small>
                    </div>
                    <div v-if="c.horario" class="hor">{{ c.hora_entrada }}<span class="ate">–{{ c.hora_saida }}</span></div>
                    <div v-else-if="c.feriado && c.feriado.aplicado" class="hor muted">Feriado</div>
                    <div v-else-if="c.com_escala || c.excecao" class="hor muted">Folga</div>
                    <div v-if="c.feriado" class="tag-fer" :class="{ ignorado: !c.feriado.aplicado }"><i class="bi bi-flag-fill"></i> {{ c.feriado.nome }}</div>
                    <div v-if="c.excecao" class="tag-exc"><i class="bi bi-pencil-fill"></i> {{ c.excecao.tipo === 'F' ? 'folga avulsa' : 'especial' }}</div>
                    <div v-else-if="c.justificativa" class="tag-just"><i class="bi bi-journal-check"></i> justificado</div>
                  </template>
                </div>
              </div>
              <div class="d-flex justify-content-between small text-muted mt-2 flex-wrap gap-2">
                <span>Clique num dia para mudar o horário previsto.</span>
                <span>Previsto no mês: <b class="text-body">{{ hhmm(totalMes) }}</b> em {{ diasTrabalho }} dia(s)</span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Lateral: escala e exceções -->
      <div class="col-xl-4">
        <div class="card mb-3" v-if="idcolaborador">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>Escala do colaborador</strong>
            <button class="btn btn-sm btn-light" @click="abrirVigencia()"><i class="bi bi-plus"></i> Nova vigência</button>
          </div>
          <ul class="list-group list-group-flush">
            <li v-if="!vinculos.length" class="list-group-item text-muted small">Sem jornada definida.</li>
            <li v-for="v in vinculosOrdenados" :key="v.id" class="list-group-item vinc" :class="{ vigente: v.vigente }">
              <div class="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <div class="fw-semibold">{{ v.nomejornada }} <span v-if="v.vigente" class="badge badge-suave ms-1">vigente</span></div>
                  <small class="text-muted">
                    {{ v.dtinicio <= '2000-01-01' ? 'desde o início' : 'de ' + dataBR(v.dtinicio) }}{{ v.dtfim ? ' até ' + dataBR(v.dtfim) : '' }}
                  </small>
                  <div v-if="v.base_texto" class="small text-muted"><i class="bi bi-arrow-repeat"></i> {{ v.base_texto }}</div>
                  <div class="small desc">{{ v.descricao }}</div>
                </div>
                <div class="d-flex gap-1">
                  <button class="btn btn-sm btn-light" @click="abrirVigencia(v)" title="Editar"><i class="bi bi-pencil"></i></button>
                  <button class="btn btn-sm btn-light text-danger" @click="excluirVigencia(v)" title="Excluir" :disabled="vinculos.length <= 1"><i class="bi bi-trash"></i></button>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <strong>Exceções do mês{{ idcolaborador ? '' : ' (todos)' }}</strong>
            <div class="form-check form-switch m-0 small">
              <input class="form-check-input" type="checkbox" id="vercanc" v-model="verCancelados" @change="carregarExcecoes">
              <label class="form-check-label m-0" for="vercanc">canceladas</label>
            </div>
          </div>
          <ul class="list-group list-group-flush">
            <li v-if="!excecoes.length" class="list-group-item text-muted small">Nenhuma exceção no mês.</li>
            <li v-for="e in excecoes" :key="e.id" class="list-group-item exc-item" :class="{ cancelada: e.cancelado }">
              <div class="d-flex justify-content-between gap-2">
                <div>
                  <div class="fw-semibold">{{ dataBR(e.data) }} <small class="text-muted">{{ e.dia.substring(0, 3) }}</small>
                    <span :class="['badge ms-1', e.tipo === 'F' ? 'text-bg-secondary' : 'text-bg-warning']">{{ e.horario }}</span>
                  </div>
                  <div v-if="!idcolaborador" class="small">{{ e.colaborador }}</div>
                  <div class="small text-muted">{{ e.motivo }}</div>
                  <div class="small text-muted fst-italic">
                    {{ e.usuario || '—' }} em {{ dataBR(e.dh_registro) }}
                    <template v-if="e.cancelado"> · cancelada por {{ e.usuario_cancelamento || '—' }}</template>
                  </div>
                </div>
                <button v-if="!e.cancelado" class="btn btn-sm btn-light text-danger align-self-start" title="Cancelar exceção" @click="cancelarExcecao(e)">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Modal: exceção -->
    <div class="modal fade" ref="mExc" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Exceção na escala</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <div v-if="exc.previsto" class="alert alert-light border small py-2">
              Previsto pela escala em {{ dataBR(exc.datainicio) }}: <b>{{ exc.previsto }}</b>
            </div>
            <div class="btn-group w-100 mb-3" role="group">
              <button type="button" :class="['btn', exc.tipo === 'H' ? 'btn-info' : 'btn-light']" @click="exc.tipo = 'H'">
                <i class="bi bi-clock"></i> Horário especial / trabalho
              </button>
              <button type="button" :class="['btn', exc.tipo === 'F' ? 'btn-info' : 'btn-light']" @click="exc.tipo = 'F'">
                <i class="bi bi-cup-hot"></i> Folga
              </button>
            </div>
            <div class="row g-2">
              <div class="col-6"><label>Data</label><input type="date" v-model="exc.datainicio" class="form-control"></div>
              <div class="col-6"><label>Repetir até (opcional)</label><input type="date" v-model="exc.datafim" :min="exc.datainicio" class="form-control"></div>
              <div class="col-12" v-if="exc.datafim && exc.datafim > exc.datainicio">
                <label>Só nestes dias da semana (opcional)</label>
                <div class="d-flex gap-1 flex-wrap">
                  <button v-for="d in [1, 2, 3, 4, 5, 6, 0]" :key="d" type="button"
                          :class="['btn btn-sm', exc.dias_semana.includes(d) ? 'btn-info' : 'btn-light']" @click="alternarDia(d)">
                    {{ SEMANA[d] }}
                  </button>
                </div>
              </div>
              <template v-if="exc.tipo === 'H'">
                <div class="col-6 col-md-3"><label>Entrada</label><input type="time" v-model="exc.hora_entrada" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Início int.</label><input type="time" v-model="exc.hora_inicio_intervalo" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Fim int.</label><input type="time" v-model="exc.hora_fim_intervalo" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Saída</label><input type="time" v-model="exc.hora_saida" class="form-control"></div>
              </template>
              <div class="col-12">
                <label>Motivo</label>
                <input v-model.trim="exc.motivo" maxlength="150" class="form-control" placeholder="Ex.: consulta médica pela manhã, compensação, evento...">
                <small class="text-muted">Aparece nas ocorrências do espelho de ponto.</small>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button v-if="exc.idexistente" class="btn btn-light text-danger me-auto" @click="cancelarExcecao({ id: exc.idexistente }, true)">
              <i class="bi bi-arrow-counterclockwise"></i> Voltar ao previsto
            </button>
            <button class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button class="btn btn-info" @click="salvarExcecao" :disabled="salvando">Salvar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: troca de folga -->
    <div class="modal fade" ref="mTroca" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Trocar folga</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted">
              O dia de trabalho vira folga e o dia de folga passa a ser trabalhado com o mesmo horário.
              Ex.: no sábado alternado, trabalhar no dia 24 em vez do dia 17.
            </p>
            <div class="row g-2">
              <div class="col-6"><label>Deixa de trabalhar em</label><input type="date" v-model="troca.data_de" class="form-control"></div>
              <div class="col-6"><label>Trabalha em</label><input type="date" v-model="troca.data_para" class="form-control"></div>
              <div class="col-12">
                <div class="form-check mt-1">
                  <input class="form-check-input" type="checkbox" id="trocaoutro" v-model="troca.outroHorario">
                  <label class="form-check-label" for="trocaoutro">Usar outro horário no dia trabalhado</label>
                </div>
              </div>
              <template v-if="troca.outroHorario">
                <div class="col-6 col-md-3"><label>Entrada</label><input type="time" v-model="troca.hora_entrada" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Início int.</label><input type="time" v-model="troca.hora_inicio_intervalo" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Fim int.</label><input type="time" v-model="troca.hora_fim_intervalo" class="form-control"></div>
                <div class="col-6 col-md-3"><label>Saída</label><input type="time" v-model="troca.hora_saida" class="form-control"></div>
              </template>
              <div class="col-12"><label>Motivo (opcional)</label><input v-model.trim="troca.motivo" maxlength="100" class="form-control"></div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button class="btn btn-info" @click="salvarTroca" :disabled="salvando">Trocar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal: vigência da jornada -->
    <div class="modal fade" ref="mVig" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ vig.id ? 'Editar vigência' : 'Nova vigência de jornada' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <div class="row g-2">
              <div class="col-12">
                <label>Jornada</label>
                <select v-model.number="vig.idjornada" class="form-select">
                  <option :value="0" disabled>Selecione</option>
                  <option v-for="j in jornadas" :key="j.id" :value="j.id">{{ j.nome }} · {{ j.escala }}</option>
                </select>
                <small v-if="jornadaVig" class="text-muted">{{ jornadaVig.descricao }}</small>
              </div>
              <div class="col-6">
                <label>Vale a partir de</label>
                <input type="date" v-model="vig.dtinicio" class="form-control">
              </div>
              <div class="col-6" v-if="precisaBase">
                <label>{{ jornadaVig.tipo === 1 ? 'Dia 1 do ciclo' : 'Uma data da semana 1' }}</label>
                <input type="date" v-model="vig.dtbase" class="form-control">
              </div>
              <div class="col-12" v-if="precisaBase">
                <div class="alert alert-light border small py-2 mb-0">
                  <template v-if="jornadaVig.tipo === 1">
                    Informe o primeiro dia do ciclo (ex.: o dia de um plantão). Os dias seguintes se repetem a cada {{ jornadaVig.ciclo_dias }} dias.
                  </template>
                  <template v-else>
                    Informe qualquer data da semana que é a <b>semana 1</b> da escala (ex.: um sábado em que o colaborador trabalha).
                    Para colaboradores que revezam o sábado, use semanas diferentes.
                  </template>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button class="btn btn-info" @click="salvarVigencia" :disabled="salvando">Salvar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
//Eu sou o caminho, a verdade e a vida. Ninguém vem ao Pai senão por mim. (João 14:6);
import Swal from 'sweetalert2'
import { Modal } from 'bootstrap'
import util from '@/util'

const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function iso(d) { return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().substring(0, 10) }

export default {
  name: 'EscalasView',
  data() {
    const h = new Date()
    return {
      SEMANA,
      hoje: iso(h),
      mes: iso(h).substring(0, 7),
      idcolaborador: null,
      calendario: [],
      vinculos: [],
      excecoes: [],
      jornadas: [],
      verCancelados: false,
      carregando: false,
      salvando: false,
      modais: {},
      exc: this.novaExcecao(),
      troca: {},
      vig: {},
    }
  },
  computed: {
    periodo() {
      const [a, m] = this.mes.split('-').map(Number)
      return { ini: this.mes + '-01', fim: iso(new Date(a, m, 0)) }
    },
    tituloMes() {
      const [a, m] = this.mes.split('-').map(Number)
      const t = new Date(a, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      return t.charAt(0).toUpperCase() + t.slice(1)
    },
    celulas() {
      if (!this.calendario.length) return []
      const out = Array(this.calendario[0].dow).fill(null).concat(this.calendario)
      while (out.length % 7) out.push(null)
      return out
    },
    temEscala() { return this.calendario.some(c => c.com_escala) },
    totalMes() { return this.calendario.reduce((a, c) => a + (c.minutos || 0), 0) },
    diasTrabalho() { return this.calendario.filter(c => c.horario).length },
    vinculosOrdenados() { return [...this.vinculos].reverse() },
    jornadaVig() { return this.jornadas.find(j => j.id === this.vig.idjornada) || null },
    precisaBase() { const j = this.jornadaVig; return j && (j.tipo === 1 || j.ciclo_dias > 7) },
  },
  watch: {
    mes() { this.carregarTudo() },
  },
  mounted() {
    const vself = this
    const $sel = $('#esc-colab').select2({
      ajax: {
        url: util.servidor + '/controller/select2sql.php',
        dataType: 'json',
        delay: 250,
        data: (params) => ({ q: params.term, idempresa: vself.$store.state.usuario.idempresa, tabela: 'tbcolaboradores' }),
        processResults: (data) => ({ results: data }),
      },
      allowClear: true,
      placeholder: 'Selecione o colaborador',
      minimumInputLength: 0,
      theme: 'bootstrap',
    }).on('change', function () {
      vself.idcolaborador = $(this).val() ? parseInt($(this).val()) : null
      vself.carregarTudo()
    })
    this.carregarJornadas()

    // Vindo do cadastro do colaborador: /escalas?colaborador=ID
    const q = parseInt(this.$route.query.colaborador)
    if (q) {
      this.req('/controller/colaboradores.php', 'listagem', [
        { parametro: 'filtro', valor: JSON.stringify({ campo: 'id', valor: q }) }, { parametro: 'limit', valor: 1 },
      ]).then(r => {
        const c = Array.isArray(r.data) && r.data[0]
        if (c) $sel.append(new Option(c.nome, c.id, true, true)).trigger('change')
      })
    } else {
      this.carregarExcecoes()
    }
  },
  beforeUnmount() {
    if ($('#esc-colab').data('select2')) $('#esc-colab').select2('destroy')
    Object.values(this.modais).forEach(m => m.dispose())
  },
  methods: {
    dataBR: (v) => util.dataBR(v),
    hhmm(min) { min = Math.round(min); return String(Math.floor(min / 60)).padStart(2, '0') + ':' + String(min % 60).padStart(2, '0') },
    req(url, op, extra = []) {
      return util.reqaxios([{ parametro: 'op', valor: op }, { parametro: 'idempresa', valor: this.$store.state.usuario.idempresa }, ...extra],
                           'post', util.servidor + url)
    },
    esc(op, extra = []) { return this.req('/controller/escalas.php', op, extra) },
    modal(nome) {
      if (!this.modais[nome]) this.modais[nome] = new Modal(this.$refs[nome])
      return this.modais[nome]
    },
    mudarMes(n) {
      const [a, m] = this.mes.split('-').map(Number)
      this.mes = iso(new Date(a, m - 1 + n, 1)).substring(0, 7)
    },
    dica(c) {
      const p = [util.dataBR(c.data)]
      if (c.nomejornada) p.push(c.nomejornada + (c.rotulo ? ' (' + c.rotulo + ')' : ''))
      p.push(c.horario || (c.com_escala || c.excecao ? 'Folga' : 'Sem jornada'))
      if (c.feriado) p.push((c.feriado.tipo === 'P' ? 'Ponto facultativo: ' : 'Feriado: ') + c.feriado.nome + (c.feriado.aplicado ? '' : ' (esta jornada não segue feriados)'))
      if (c.excecao) p.push('Exceção: ' + c.excecao.motivo)
      if (c.justificativa) p.push('Justificativa: ' + c.justificativa)
      return p.join('\n')
    },
    async carregarTudo() {
      if (!this.idcolaborador) { this.calendario = []; this.vinculos = []; return this.carregarExcecoes() }
      this.carregando = true
      try {
        await Promise.all([this.carregarCalendario(), this.carregarVinculos(), this.carregarExcecoes()])
      } finally {
        this.carregando = false
      }
    },
    async carregarCalendario() {
      const r = await this.esc('calendario', [
        { parametro: 'idcolaborador', valor: this.idcolaborador },
        { parametro: 'datainicio', valor: this.periodo.ini }, { parametro: 'datafim', valor: this.periodo.fim },
      ])
      this.calendario = Array.isArray(r.data) ? r.data : []
      if (!Array.isArray(r.data)) util.respostaOk(r.data)
    },
    async carregarVinculos() {
      const r = await this.esc('vinculos', [{ parametro: 'idcolaborador', valor: this.idcolaborador }])
      this.vinculos = Array.isArray(r.data) ? r.data : []
    },
    async carregarExcecoes() {
      const extra = [
        { parametro: 'datainicio', valor: this.periodo.ini }, { parametro: 'datafim', valor: this.periodo.fim },
        { parametro: 'cancelados', valor: this.verCancelados ? 1 : 0 },
      ]
      if (this.idcolaborador) extra.push({ parametro: 'idcolaborador', valor: this.idcolaborador })
      const r = await this.esc('excecoes', extra)
      this.excecoes = Array.isArray(r.data) ? r.data : []
    },
    async carregarJornadas() {
      const r = await this.req('/controller/jornadas.php', 'listagem', [{ parametro: 'limit', valor: 1000 }, { parametro: 'offset', valor: 0 }])
      this.jornadas = Array.isArray(r.data) ? r.data : []
    },

    // ------------------------------------------------------------ exceções
    novaExcecao() {
      return { datainicio: '', datafim: '', tipo: 'H', hora_entrada: '08:00', hora_inicio_intervalo: '', hora_fim_intervalo: '',
               hora_saida: '12:00', motivo: '', dias_semana: [], previsto: '', idexistente: null }
    },
    abrirExcecao(c) {
      const e = this.novaExcecao()
      e.datainicio = c ? c.data : this.hoje
      if (c) {
        e.previsto = c.excecao ? '' : (c.horario || 'Folga')
        e.idexistente = c.excecao ? c.excecao.id : null
        if (c.horario) {
          Object.assign(e, { hora_entrada: c.hora_entrada, hora_inicio_intervalo: c.hora_inicio_intervalo,
                             hora_fim_intervalo: c.hora_fim_intervalo, hora_saida: c.hora_saida })
        }
        e.tipo = c.excecao ? c.excecao.tipo : 'H'
        if (c.excecao) e.motivo = c.excecao.motivo
      }
      this.exc = e
      this.modal('mExc').show()
    },
    alternarDia(d) {
      const i = this.exc.dias_semana.indexOf(d)
      if (i >= 0) this.exc.dias_semana.splice(i, 1); else this.exc.dias_semana.push(d)
    },
    async salvarExcecao() {
      const e = this.exc
      if (!e.datainicio) return util.notif('error', 'Atenção', 'Informe a data.')
      if (e.tipo === 'H' && (!e.hora_entrada || !e.hora_saida)) return util.notif('error', 'Atenção', 'Informe entrada e saída.')
      if (!e.motivo) return util.notif('error', 'Atenção', 'Informe o motivo. Ele aparece no espelho de ponto.')
      this.salvando = true
      try {
        const reg = { ...e, idcolaborador: this.idcolaborador, dias_semana: e.datafim > e.datainicio ? e.dias_semana : [] }
        const r = await this.esc('salvarexcecao', [{ parametro: 'registro', valor: JSON.stringify(reg) }])
        if (util.respostaOk(r.data)) {
          this.modal('mExc').hide()
          util.notif('success', parseInt(r.data) > 1 ? r.data + ' dias alterados' : 'Exceção registrada', '')
          this.carregarTudo()
        }
      } finally {
        this.salvando = false
      }
    },
    async cancelarExcecao(e, doModal = false) {
      const ok = await Swal.fire({ title: 'Cancelar a exceção?', text: 'O dia volta ao horário previsto pela jornada. O registro fica guardado como cancelado.',
                                   icon: 'question', showCancelButton: true, confirmButtonText: 'Cancelar exceção', cancelButtonText: 'Voltar' })
      if (!ok.isConfirmed) return
      const r = await this.esc('cancelarexcecao', [{ parametro: 'id', valor: e.id }])
      if (util.respostaOk(r.data)) {
        if (doModal) this.modal('mExc').hide()
        this.carregarTudo()
      }
    },

    // ------------------------------------------------------- troca de folga
    abrirTroca() {
      this.troca = { data_de: '', data_para: '', outroHorario: false, hora_entrada: '08:00', hora_inicio_intervalo: '',
                     hora_fim_intervalo: '', hora_saida: '12:00', motivo: '' }
      this.modal('mTroca').show()
    },
    async salvarTroca() {
      const t = this.troca
      if (!t.data_de || !t.data_para) return util.notif('error', 'Atenção', 'Informe as duas datas.')
      this.salvando = true
      try {
        const reg = { idcolaborador: this.idcolaborador, data_de: t.data_de, data_para: t.data_para, motivo: t.motivo }
        if (t.outroHorario) Object.assign(reg, { hora_entrada: t.hora_entrada, hora_inicio_intervalo: t.hora_inicio_intervalo,
                                                 hora_fim_intervalo: t.hora_fim_intervalo, hora_saida: t.hora_saida })
        const r = await this.esc('trocarfolga', [{ parametro: 'registro', valor: JSON.stringify(reg) }])
        if (util.respostaOk(r.data)) {
          this.modal('mTroca').hide()
          util.notif('success', 'Folga trocada', '')
          this.carregarTudo()
        }
      } finally {
        this.salvando = false
      }
    },

    // ------------------------------------------------------------ vigência
    abrirVigencia(v) {
      this.vig = v
        ? { id: v.id, idjornada: v.idjornada, dtinicio: v.dtinicio, dtbase: v.dtbase }
        : { id: 0, idjornada: this.vinculos.length ? this.vinculos[this.vinculos.length - 1].idjornada : 0, dtinicio: this.hoje, dtbase: this.hoje }
      this.modal('mVig').show()
    },
    async salvarVigencia() {
      const v = this.vig
      if (!v.idjornada) return util.notif('error', 'Atenção', 'Selecione a jornada.')
      if (!v.dtinicio) return util.notif('error', 'Atenção', 'Informe a data de início.')
      this.salvando = true
      try {
        const reg = { ...v, idcolaborador: this.idcolaborador, dtbase: this.precisaBase ? v.dtbase : v.dtinicio }
        const r = await this.esc('salvarvinculo', [{ parametro: 'registro', valor: JSON.stringify(reg) }])
        if (util.respostaOk(r.data)) {
          this.modal('mVig').hide()
          this.carregarTudo()
        }
      } finally {
        this.salvando = false
      }
    },
    async excluirVigencia(v) {
      const ok = await Swal.fire({ title: 'Excluir esta vigência?', text: `${v.nomejornada} a partir de ${util.dataBR(v.dtinicio)}. O período passa a usar a vigência anterior.`,
                                   icon: 'warning', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar' })
      if (!ok.isConfirmed) return
      const r = await this.esc('excluirvinculo', [{ parametro: 'id', valor: v.id }])
      if (util.respostaOk(r.data)) this.carregarTudo()
    },
  },
}
</script>

<style scoped lang="scss">
.legenda { font-size: .75rem; color: var(--ck-texto-2); display: flex; align-items: center; gap: 6px; }
.lg { width: 12px; height: 12px; border-radius: 3px; display: inline-block; margin-left: 6px; }
.lg.trab { background: var(--ck-primaria-suave); }
.lg.folga { background: #f1f3f2; border: 1px solid var(--ck-borda); }
.lg.exc { background: #fff3d9; border: 1px solid var(--ck-acento); }
.lg.fer { background: #eee9fb; border: 1px solid #b9a8ea; }

.cal { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 6px; }
.cal-cab { font-size: .7rem; text-transform: uppercase; letter-spacing: .05em; color: var(--ck-texto-2); font-weight: 700; text-align: center; padding-bottom: 2px; }
.cal-dia {
  min-height: 78px; border-radius: 10px; padding: 6px 7px; background: var(--ck-primaria-suave); cursor: pointer;
  border: 1px solid transparent; transition: transform .1s, box-shadow .1s; overflow: hidden;
}
.cal-dia:hover { box-shadow: 0 0 0 2px rgba(19, 83, 74, .25); }
.cal-dia.vazio { background: transparent; cursor: default; box-shadow: none; }
.cal-dia.folga { background: #f4f6f5; }
.cal-dia.fer { background: #f4f1fd; border-color: #d8cdf5; }
.cal-dia.exc { background: #fff7e3; border-color: var(--ck-acento); }
.cal-dia.hoje .num-dia { background: var(--ck-primaria); color: #fff; border-radius: 6px; padding: 0 5px; }
.num-dia { font-weight: 700; font-size: .85rem; }
.rot { font-size: .62rem; color: var(--ck-texto-2); font-weight: 600; }
.hor { font-size: .78rem; font-weight: 600; color: var(--ck-primaria); margin-top: 6px; font-variant-numeric: tabular-nums; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.hor.muted { color: #9aa8a3; font-weight: 500; }
.tag-exc { font-size: .66rem; color: #8a5a00; font-weight: 600; margin-top: 2px; }
.tag-fer { font-size: .66rem; color: #5b3fb3; font-weight: 600; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tag-fer.ignorado { color: #9aa8a3; font-weight: 500; }
.tag-fer i { font-size: .6rem; }
.tag-just { font-size: .66rem; color: var(--ck-info); font-weight: 600; margin-top: 2px; }
.tag-exc i, .tag-just i { font-size: .6rem; }
@media (max-width: 576px) {
  .cal { gap: 3px; }
  .cal-dia { min-height: 58px; padding: 4px; }
  .hor { font-size: .62rem; }
  .tag-exc, .tag-just, .tag-fer, .rot, .ate, .hor.muted { display: none; }
}
.vinc.vigente { background: #f7fbfa; }
.vinc .desc { color: var(--ck-texto-2); margin-top: 2px; }
.exc-item.cancelada { opacity: .55; text-decoration: line-through; }
</style>
